import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { pmResetApi } from '../../lib/pmApi';
import Logo from '../../components/Logo';
import { formatApiErrorDetail } from '../../contexts/AuthContext';
import { KeyRound, CheckCircle2, AlertTriangle } from 'lucide-react';

const PmResetPassword = () => {
  const { token } = useParams();
  const navigate = useNavigate();
  const [state, setState] = useState('checking'); // checking | ready | invalid | done
  const [info, setInfo] = useState(null);
  const [error, setError] = useState('');
  const [pw, setPw] = useState('');
  const [pw2, setPw2] = useState('');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    pmResetApi.validate(token)
      .then((d) => { setInfo(d); setState('ready'); })
      .catch((e) => { setError(formatApiErrorDetail(e.response?.data?.detail) || 'This link is invalid.'); setState('invalid'); });
  }, [token]);

  const submit = async (e) => {
    e.preventDefault();
    setError('');
    if (pw.length < 8) { setError('Password must be at least 8 characters.'); return; }
    if (pw !== pw2) { setError('Passwords do not match.'); return; }
    setSaving(true);
    try {
      await pmResetApi.complete(token, pw);
      setState('done');
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || 'Could not set your password.');
    } finally {
      setSaving(false);
    }
  };

  const isInvite = info?.purpose === 'invite';

  return (
    <div className="min-h-screen bg-[#0A0A0A] flex items-center justify-center p-6" data-testid="pm-reset-page">
      <div className="w-full max-w-md">
        <div className="text-center mb-8 flex flex-col items-center">
          <Logo size="lg" />
          <p className="text-[#D4AF37] text-xs font-bold tracking-[0.3em] mt-4">PROPERTY MANAGER PORTAL</p>
        </div>

        <div className="bg-[#141414] border border-[#262626] rounded-md p-8">
          {state === 'checking' && (
            <p className="text-[#A3A3A3] text-center" data-testid="pm-reset-checking">Checking your link…</p>
          )}

          {state === 'invalid' && (
            <div className="text-center space-y-4" data-testid="pm-reset-invalid">
              <AlertTriangle className="w-10 h-10 text-[#EF4444] mx-auto" />
              <p className="text-white">{error}</p>
              <p className="text-[#A3A3A3] text-sm">Please contact NexFlow to request a new link.</p>
            </div>
          )}

          {state === 'done' && (
            <div className="text-center space-y-4" data-testid="pm-reset-done">
              <CheckCircle2 className="w-10 h-10 text-[#22C55E] mx-auto" />
              <p className="text-white text-lg font-medium">You're all set!</p>
              <p className="text-[#A3A3A3] text-sm">Your password has been {isInvite ? 'created' : 'reset'}. You can now sign in.</p>
              <button
                data-testid="pm-reset-goto-login"
                onClick={() => navigate('/login')}
                className="w-full bg-[#D4AF37] text-[#0A0A0A] h-11 rounded font-medium hover:bg-[#F3E5AB]"
              >
                Go to sign in
              </button>
            </div>
          )}

          {state === 'ready' && (
            <form onSubmit={submit} className="space-y-5" data-testid="pm-reset-form">
              <div className="flex items-center gap-2 text-white">
                <KeyRound className="w-5 h-5 text-[#D4AF37]" />
                <span className="font-medium">{isInvite ? 'Set your password' : 'Reset your password'}</span>
              </div>
              {info?.email && <p className="text-[#A3A3A3] text-sm">For <span className="text-white font-mono">{info.email}</span></p>}

              {error && (
                <div data-testid="pm-reset-error" className="bg-[#7F1D1D] text-white p-3 rounded-md text-sm">{error}</div>
              )}

              <div className="space-y-2">
                <label className="text-white text-sm">New password</label>
                <input
                  data-testid="pm-reset-password"
                  type="password"
                  value={pw}
                  onChange={(e) => setPw(e.target.value)}
                  required
                  className="w-full bg-[#0A0A0A] border border-[#262626] rounded h-11 px-3 text-white text-sm focus:ring-1 focus:ring-[#D4AF37]"
                />
              </div>
              <div className="space-y-2">
                <label className="text-white text-sm">Confirm new password</label>
                <input
                  data-testid="pm-reset-password-confirm"
                  type="password"
                  value={pw2}
                  onChange={(e) => setPw2(e.target.value)}
                  required
                  className="w-full bg-[#0A0A0A] border border-[#262626] rounded h-11 px-3 text-white text-sm focus:ring-1 focus:ring-[#D4AF37]"
                />
              </div>
              <button
                data-testid="pm-reset-submit"
                type="submit"
                disabled={saving}
                className="w-full bg-[#D4AF37] text-[#0A0A0A] h-11 rounded font-medium hover:bg-[#F3E5AB] disabled:opacity-50"
              >
                {saving ? 'Saving…' : (isInvite ? 'Create password' : 'Reset password')}
              </button>
            </form>
          )}
        </div>
      </div>
    </div>
  );
};

export default PmResetPassword;
